import { CommonStoreActions } from '@/utils/storeHelpers'
import type { ISearchActions, ISearchState, SearchFilterOption, DocActionPayload, DocActionProgressPayload, DocActionResultPayload, SaveSearchPayload, SuggestRecord, } from '../types.ts'
import { apiSearchResults, apiGetICSCodeOptions, apiSaveSearch, apiSuggestStandards, apiStandardSearch, } from '../api.ts'
import actionsStandardSearch from './actionsStandardSearch.ts';
import actionsCollectionSearch from './actionsCollectionSearch.ts';
import actionsUpdatesSearch from './actionsUpdatesSearch.ts';
import { STANDARDS_SEARCH_TAB, COLLECTIONS_SEARCH_TAB, UPDATES_SEARCH_TAB } from '@/components/Search/constants.ts';


const standardFilterKeys = [
  'documentNumber',
  'publisher',
  'status',
  'country',
  'dataset',
  'crossReference',
  'descriptorField',
  'descriptorGroup',
  'descriptorSubGroup',
  'language',
  'year',
  'searchIn',
  "icsCode"
];

const collectionFilterKeys = [
  'documentNumber',
  'publisher',
  'collections',
  'companyCollection',
  'documentType',
  'status',
  'tags',
  'notes',
  'descriptorField',
  'descriptorGroup',
  'descriptorSubGroup',
  "icsCode"
];

const actions: ISearchActions = {
  ...CommonStoreActions,
  ...actionsStandardSearch,
  ...actionsCollectionSearch,
  ...actionsUpdatesSearch,
  fetchStandardSuggestions: async function (this, query: string): Promise<SuggestRecord[]> {
    if (!query) {
      return []
    }

    try {
      const suggestApiAbortController = this.addApiAbortController('standardSuggestApi')
      const records = await apiSuggestStandards(query, suggestApiAbortController)
      return records || []
    } catch (error) {
      console.log('Failed to fetch standard suggestions', error)
      return []
    }
  },
  clearSearchBarFilters: function (this, searchType, ignoreFields: string[] = []) {
    // Reset all search filters to their initial state
    if(searchType == STANDARDS_SEARCH_TAB) {
      for (const key of standardFilterKeys) {
        if(ignoreFields.includes(key)) {
          continue;
        }
        if(key === 'documentNumber' || key === 'crossReference' || key === 'icsCode') {
          (this as any)[`${STANDARDS_SEARCH_TAB}.${key}`] = '';
        } else {
          (this as any)[`${STANDARDS_SEARCH_TAB}.${key}`] = [];
        }
      }
    } else if (searchType == COLLECTIONS_SEARCH_TAB) {
       for (const key of collectionFilterKeys) {
        if(ignoreFields.includes(key)) {
          continue;
        }
        if(key === 'documentNumber' || key === 'notes' || key === 'icsCode') {
          (this as any)[`${COLLECTIONS_SEARCH_TAB}.${key}`] = '';
        } else {
          (this as any)[`${COLLECTIONS_SEARCH_TAB}.${key}`] = [];
        }
      }
    }
    else if (searchType == UPDATES_SEARCH_TAB) {
          this[`${UPDATES_SEARCH_TAB}.what`] = [];
          this[`${UPDATES_SEARCH_TAB}.publisher`] = [];
          this[`${UPDATES_SEARCH_TAB}.when`] = [];
          this[`${UPDATES_SEARCH_TAB}.dataset`] = [];
          this[`${UPDATES_SEARCH_TAB}.dateStart`] = null;
          this[`${UPDATES_SEARCH_TAB}.dateEnd`] = null;
    }
  },
  clearSearchFacetFilters: function (this, searchType, ignoreFields: string[] = []) {
    // Reset all search filters to their initial state
    if(searchType == STANDARDS_SEARCH_TAB) {
      for (const key of standardFilterKeys) {
        if(ignoreFields.includes(key)) {
          continue;
        }
        //if documentNumber or crossReference set it to ''
        if(key === 'documentNumber' || key === 'crossReference') {
          (this as any)[`${STANDARDS_SEARCH_TAB}.facetFilter.${key}`] = '';
        } else {
          (this as any)[`${STANDARDS_SEARCH_TAB}.facetFilter.${key}`] = [];
        }
      }
    } else if (searchType == COLLECTIONS_SEARCH_TAB) {
       for (const key of collectionFilterKeys) {
        if(ignoreFields.includes(key)) {
          continue;
        }
        //if documentNumber or notes set it to ''
        if(key === 'documentNumber' || key === 'notes') {
          (this as any)[`${COLLECTIONS_SEARCH_TAB}.facetFilter.${key}`] = '';
        } else {
          (this as any)[`${COLLECTIONS_SEARCH_TAB}.facetFilter.${key}`] = [];
        }
      }
    }
    else if (searchType == UPDATES_SEARCH_TAB) {
        this[`${UPDATES_SEARCH_TAB}.facetFilter.what`] = [];
        this[`${UPDATES_SEARCH_TAB}.facetFilter.publisher`] = [];
        this[`${UPDATES_SEARCH_TAB}.facetFilter.when`] = [];
        this[`${UPDATES_SEARCH_TAB}.facetFilter.dataset`] = [];
        this[`${UPDATES_SEARCH_TAB}.facetFilter.dateStart`] = null;
        this[`${UPDATES_SEARCH_TAB}.facetFilter.dateEnd`] = null;
    }
  },
  removeFilter: function (this, key: keyof ISearchState, item: SearchFilterOption) {
    const arr = this[key]

    if (Array.isArray(arr)) {
      const index = arr.findIndex(f => f.id === item.id)  // match by id
      if (index > -1) {
        arr.splice(index, 1)
      }
    } else if (typeof arr === 'string') {
      // If the value is a string, clear it
      (this as any)[key] = '';
    }
  },
  setRowActionProgress: function(this, payload: DocActionProgressPayload) {
    const { searchType, action, docIds, actionProcessing, bulkActionProcessing } = payload;
    let resultState: any = (this as any)[searchType + '.resultData'];
    for (let docid of docIds) {
      for (let key of Object.keys(resultState['records'])) {
        if(resultState['records'][key]['doc_id'] == docid) {
          resultState['records'][key][action + 'Processing'] = actionProcessing;
          resultState['records'][key]['actionProcessing'] = actionProcessing || bulkActionProcessing;
          resultState['records'][key]['bulkActionProcessing'] = bulkActionProcessing;
          break;
        }
      }
    }
  },
  updateRowActionResult: function(this, payload:  DocActionResultPayload) {
    const { searchType, docId, fieldValues } = payload;
    let resultState: any = (this as any)[searchType + '.resultData'];
    for (let key of Object.keys(resultState['records'])) {
      if(resultState['records'][key]['doc_id'] == docId) {
        let docRecord = resultState['records'][key];
        resultState['records'][key] = Object.assign(docRecord, fieldValues);
        break;
      }
    }
  },
  performDocAction: async function(this, payload: DocActionPayload) {
        const { searchType, action, collectionId, docIds, isBulkAction } = payload;
        try {
            let docIdsForApi: string | string[] = docIds;
            docIdsForApi = docIds.join(',');
            this.setRowActionProgress({ searchType, action, docIds, actionProcessing: !isBulkAction, bulkActionProcessing: isBulkAction });
            let results = [];
            const searchResultActionApiAbortController = this.addApiAbortController('searchResultActionApi');
            switch(action) {
                case 'addItem':
                    results =  await apiSearchResults('addToCollection', { collectionId, docIds: docIdsForApi }, searchResultActionApiAbortController);
                    break;
                case 'remItem':
                    results =  await apiSearchResults('removeFromCollection', { collectionId, docIds: docIdsForApi }, searchResultActionApiAbortController);
                    break;
                case 'watchItem':
                    results =  await apiSearchResults('addToWatch', { collectionId, docIds: docIdsForApi }, searchResultActionApiAbortController);
                    break;
                case 'unWatchItem':
                case 'unWatchItemGlobal':
                    results =  await apiSearchResults('removeFromWatch', { collectionId, docIds: docIdsForApi }, searchResultActionApiAbortController);
                    break;
            }

            if(results.status != 'error') {
                const { collectionId: activeCollectionId, collectionName:  activeCollectionName, warnMsg,  successMsg} = results.data; // get the collection id after row action, as it can be a new collection id
                // Update the active collection details in the state
                (this as any)[searchType + '.resultData'].activeCollectionId = activeCollectionId;
                (this as any)[searchType + '.resultData'].activeCollectionName = activeCollectionName;
                // if(warnMsg && warnMsg.length) {
                //     for(let msg of warnMsg) {
                //         commit(Mutation.SET_TOASTR_MESSAGE,{ type: 'warning',  message: msg });
                //     }
                // }
                // if(successMsg && successMsg.length) {
                //     for(let msg of successMsg) {
                //         commit(Mutation.SET_TOASTR_MESSAGE,{ type: 'success',  message: msg });
                //     }
                // }
                // const getDocActionApiAbortController = this.addApiAbortController('getDocActionApi');
                results =  await apiSearchResults('getDocumentActions', { activeCollectionId, docIds: docIdsForApi });
                if(results.status != 'error' && results.data) {
                    for(const record of results.data) {
                        this.updateRowActionResult({ searchType, docId: record.doc_id, fieldValues: { actions: record.actions, collection: record.collection } });
                    }
                }
            }
            this.setRowActionProgress({ searchType, action, docIds, actionProcessing: false, bulkActionProcessing: false });
        } catch(error) {
            this.setRowActionProgress({ searchType, action, docIds, actionProcessing: false, bulkActionProcessing: false });
            console.log('Doc action error');
        }
  },
  toggleSelectItem: function(this, payload: { searchType: string; item: any }) {
    const { searchType, item } = payload;
    let resultState: any = (this as any)[searchType + '.resultData'];
    if(!resultState['selectedItems']) {
      resultState['selectedItems'] = [];
    }
    const index = resultState['selectedItems'].findIndex((i: any) => i.doc_id === item.doc_id);
    if (index > -1) {
      // Item is already selected, so remove it
      resultState['selectedItems'].splice(index, 1);
    } else {
      // Item is not selected, so add it
      resultState['selectedItems'].push(item);
    }
  },
  selectAllItems: function(this, payload: { searchType: string; selectAll: boolean }) {
    const { searchType, selectAll } = payload;
    let resultState: any = (this as any)[searchType + '.resultData'];
    if(!resultState['selectedItems']) {
      resultState['selectedItems'] = [];
    }
    if(selectAll) {
      // Select all items
      resultState['selectedItems'] = [...resultState['records']];
    } else {
      // Deselect all items
      resultState['selectedItems'] = [];
    }
  },
  toggleSearchBar: function (this) {
    this['displaySearchBar'] = !this['displaySearchBar'];
  },
  loadICSCodeOptions: async function(this, payload:  {field: string, icsCode: SearchFilterOption[], level: number}) {
    const { field, icsCode, level } = payload;
    try {
        //remove options before loading new options
        (this as any)[field] = []; //clear selected values
        if(icsCode.length > 0) {
          let options = [];
          if(level==2) {
            options = (this as any)['lookUpOptions.allDescriptorGroup'].filter((item: SearchFilterOption) => item.id.toString().startsWith(icsCode[0].id.toString()));
          }
          if(level==3) {
            options = (this as any)['lookUpOptions.allDescriptorSubGroup'].filter((item: SearchFilterOption) => item.id.toString().startsWith(icsCode[0].id.toString()));
          }
          (this as any)[field] = options;
        }
    } catch(error) {
        //remove options in case of error
        (this as any)[field] = [];
        console.log('Failed to load ISC Code ', icsCode);
    }
  },
  saveSearch: async function(this, payload: SaveSearchPayload) {
      const { searchType } = payload;
      if(searchType == STANDARDS_SEARCH_TAB) {
          return await this.saveSearchStandards(payload);
      } else if (searchType == COLLECTIONS_SEARCH_TAB) {
          return await this.saveSearchCollections(payload);
      }

  },
  saveSearchCollections: async function(this, payload: SaveSearchPayload) {
      const { saveSearchNotify, saveSearchName, searchType } = payload;
      try {
          const saveSearchApiAbortController = this.addApiAbortController('saveSearchApiAbortController');
          const params= {
            save_search_notify: (saveSearchNotify ? '1' : '0'),
            save_search_name: saveSearchName,
            save_search_sel: '0',
            onclk_saveSearch: 'save',
            nonce: this['nonce']
          }
          let results =  await apiSaveSearch(this[`${searchType}.resultData`].searchPageUrl, params, saveSearchApiAbortController);
          if(results) {
              this[`${searchType}.resultData`].isSavedSearch = 1;
              return results;
          } else {
              console.log('error', results);
              return '';
          }
      } catch(error: any) {
          console.log('saved search failed');
          return error.response?.data;
      }
  },
  saveSearchStandards: async function(this, payload: SaveSearchPayload) {
      const { saveSearchNotify, saveSearchName, searchType } = payload;
      try {
          
          const params = this.getStandardSearchParams(true, 1);
          params['saveSearch'] = true;
          const standardSearchApiAbortController = this.addApiAbortController('standardSearchApi');
          const resultForSaveSearch: any = await apiStandardSearch(params, standardSearchApiAbortController);

          const saveSearchApiAbortController = this.addApiAbortController('saveSearchApiAbortController');
          const saveSearchParams = {
            save_search_notify: (saveSearchNotify ? '1' : '0'),
            save_search_name: saveSearchName,
            save_search_sel: '0',
            save_search_context: resultForSaveSearch.searchContext??'',
            save_search_params: JSON.stringify(params??{}),
            save_search_query: resultForSaveSearch.elasticQuery??'',
            save_search_docIds: resultForSaveSearch.docIds?.join(',')??'',
            save_search_es_preference: resultForSaveSearch.esPreference??'',
            onclk_saveSearchStandards: 'save',
            nonce: this['nonce']
          };
          let results =  await apiSaveSearch('/management/search/index', saveSearchParams, saveSearchApiAbortController);
          if(results) {
              //store the params in savedSearches so we can compare later for isSavedSearchMatch
              const savedSearchEntry = {
                context: saveSearchParams.save_search_context ?? '',
                params: JSON.stringify(params ?? {})
              };
              const savedSearchList = Array.isArray((this as any)['standards.savedSearches'])
                ? (this as any)['standards.savedSearches']
                : [];
              savedSearchList.push(savedSearchEntry);
              (this as any)['standards.savedSearches'] = savedSearchList;
              this[`${searchType}.resultData`].isSavedSearch = 1;
              return results;
          } else {
              console.log('error', results);
              return '';
          }
      } catch(error: any) {
          console.log('saved search failed');
          return error.response?.data;
      }
  },
  saveSearchParamsToHistory: function (this, searchContext: string, params: any) {
    if (!searchContext) {
      console.warn("No searchContext found");
      return;
    }

    const value = JSON.stringify(params);
    localStorage.setItem(searchContext, value);
    const HISTORY_KEY = "searchHistoryKeys";
    const keys = JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
    // If this key is newly added, push it
    if (!keys.includes(searchContext)) {
      keys.push(searchContext);
    }

    while (keys.length > 10) {
      const toRemove = keys.shift(); // remove first (oldest)
      if (toRemove) {
        localStorage.removeItem(toRemove);
      }
    }
    localStorage.setItem(HISTORY_KEY, JSON.stringify(keys));
  }
}

export default actions
